import * as wjcCore from '@mescius/wijmo';
import * as wjcGrid from '@mescius/wijmo.grid';
import * as wjcMultirow from '@mescius/wijmo.grid.multirow';
import * as wjcGridFilter from '@mescius/wijmo.grid.filter';

'use strict';

import {DataService} from "./data.service";

export class OrientDBCollectionView extends wjcCore.CollectionView {
  // vendored 化に伴う TS 移行措置: 旧実装は宣言なしで動的プロパティを使う
  [key: string]: any;
  _api: any;
  _database: string;
  _className: string;
  _group: string;
  _query: string;
  _count: string;
  _fetchPlan: string;
  _countOnServer: boolean = true;
  _sortOnServer: boolean;
  _filterOnServer: boolean;
  _itemsSourceFunction: Function;
  _preSaveChangesFunction: Function;
  _preDeleteItemsFunction: Function;
  _pageOnServer: boolean;
  _filterPredicate: any;
  _filterSpecial: string;
  _totalCount: number;
  _isSaving: boolean = false;
  _url: string;
  _tokenName: string;
  querySubscription: any;
  updateSubscription: any;
  deleteSubscription: any;
  _bindingMap: { [key: string]: { field: string; subField: string; dataType?: string } } = {};

  /**
   * Initializes a new instance of an @see:OrientDBCollectionView.
   *
   * @param api for orientdb service.
   * @param database name to connect.
   * @param query orientdb base query.
   * @param pageIndex initial page index.
   * @param pageSize  initial page size.
   * @param sortOnServer Whether to sort on the server or on the client.
   * @param pageOnServer Whether to page on the server or on the client.
   * @param filterOnServer Whether to filter on the server or on the client.
   */
  constructor(api?: DataService, database?: string, query?: string, className?: string, group?: string, pageIndex?: number, pageSize?: number, sortOnServer?: boolean, pageOnServer?: boolean, filterOnServer?: boolean) {
    super();
    this.clearData();
    this._api = api;
    this._database = database;
    this._query = query;
    this._count = "";
    this._className = className;
    this._group = group;
    this._pgIdx = pageIndex;
    this._pgSz = pageSize;
    this._sortOnServer = sortOnServer;
    this._pageOnServer = pageOnServer;
    this._filterOnServer = filterOnServer;
    this.sortDescriptions.collectionChanged.removeAllHandlers();
    this.sortDescriptions.collectionChanged.addHandler(this._sortDescHandler.bind(this));
    this._queryData();
    this.trackChanges = true;
  }

  /**
   * Overridden to get the total number pages.
   */
  // @ts-ignore: wijmo 5.20242 では property 宣言、旧実装は accessor override
  get pageCount(): number {
    if (this.pageSize) {
      if (this._pageOnServer) {
        return Math.ceil(this._totalCount / this.pageSize);
      } else {
        return Math.ceil(this._view.length / this.pageSize);
      }
    }
    return 1;
  }

  /**
   * Overridden to get or set the number of items to display on a page.
   */
  // @ts-ignore: wijmo 5.20242 では property 宣言、旧実装は accessor override
  get pageSize(): number {
    return this._pgSz;
  }

  // @ts-ignore: wijmo 5.20242 では property 宣言、旧実装は accessor override

  set pageSize(value: number) {
    if (wjcCore.isNumber(value) && value != this._pgSz) {
      this._pgSz = wjcCore.asInt(value);
      if (this._pageOnServer) {
        this._pgIdx = 0;
        this._idx = 0;
        this._queryData();
      } else {
        this.refresh();
      }
    }
  }

  /**
   * Overridden to move to the page at the specified index.
   *
   * @param index Index of the page to move to.
   * @return True if the page index was changed successfully.
   */
  moveToPage(index: number): boolean {

    if (!this.pageOnServer) {
      return super.moveToPage(index);
    }
    var newIndex = wjcCore.clamp(index, 0, this.pageCount - 1);
    if (newIndex != this.pageIndex) {

      // honor canChangePage
      if (!this.canChangePage) {
        wjcCore.assert(false, 'Changing pages not supported.');
      }

      // raise pageChanging
      var e = new wjcCore.PageChangingEventArgs(newIndex);
      if (this.onPageChanging(e)) {

        // change the page
        this._pgIdx = newIndex;
        this._idx = 0;
        this._queryData();
      }
    }
    return this._pgIdx == index;
  }

  /**
   * Has changes.
   */
  hasChanges(): boolean {
    if (this.itemsAdded.length > 0 || this.itemsEdited.length > 0 || this.itemsRemoved.length > 0) {
      this.filterOnServer = false;
      this.sortOnServer = false;
      this.pageOnServer = false;
      return true;
    }
    return false;
  }

  /**
   * Cancel changes.
   */
  cancelChanges() {
    super.clearChanges();
    if (!this.sortOnServer)
      this.sortOnServer = true;
    if (!this.pageOnServer)
      this.pageOnServer = true;
    if (!this.filterOnServer)
      this.filterOnServer = true;
    else
      this._queryData();
  }

  /**
   * Save changes on the server and clearChanges been tracked.
   */
  saveChanges() {
    var saveItems: any[] = [];
    var deleteItems: any[] = [];
    for (var i = 0; i < this.itemsAdded.length; i++) {
      if (!this.itemsAdded[i].hasOwnProperty("@rid")) {
        this.itemsAdded[i]["@class"] = this._className;
        this.itemsAdded[i]["@rid"] = "#-1:-1";
      }
      saveItems.push(this.itemsAdded[i]);
    }
    for (var i = 0; i < this.itemsEdited.length; i++) {
      if (!this.itemsEdited[i].hasOwnProperty("@rid")) {
        this.itemsEdited[i]["@class"] = this._className;
        this.itemsEdited[i]["@rid"] = "#-1:-1";
      }
      saveItems.push(this.itemsEdited[i]);
    }
    for (var i = 0; i < this.itemsRemoved.length; i++) {
      if (this.itemsRemoved[i].hasOwnProperty("@rid")) {
        deleteItems.push(this.itemsRemoved[i]);
      }
    }
    if (saveItems.length > 0) {
      this._saveChanges(saveItems);
    }
    if (deleteItems.length > 0) {
      this._deleteItems(deleteItems);
    }
    super.clearChanges();
    if (!this.sortOnServer) {
      this.sortOnServer = true;
    }
    if (!this.pageOnServer) {
      this.pageOnServer = true;
    }
    if (!this.filterOnServer) {
      this.filterOnServer = true;
    } else {
      if (!this._isSaving)
        this._queryData();
    }

  }

  // End of Override properties & methods
  /**
   * Gets or sets a value url.
   */
  get url(): string {
    return this._url;
  }

  set url(value: string) {
    if (value != this._url) {
      this._url = wjcCore.asString(value);
    }
  }

  // End of Override properties & methods
  /**
   * Gets or sets a value tokenName.
   */
  get tokenName(): string {
    return this._tokenName;
  }

  set tokenName(value: string) {
    if (value != this._tokenName) {
      this._tokenName = wjcCore.asString(value);
    }
  }

  // End of Override properties & methods
  /**
   * Gets or sets a value query.
   */
  get query(): string {
    return this._query;
  }

  set query(value: string) {
    if (value != this._query) {
      this._query = wjcCore.asString(value);
      this._queryData();
    }
  }

  // End of Override properties & methods
  /**
   * Gets or sets a value count.
   */
  get count(): string {
    return this._count;
  }

  set count(value: string) {
    if (value != this._count) {
      this._count = wjcCore.asString(value);
      this._queryData();
    }
  }

  /**
   * Gets or sets a value indicating whether to count on the server or on the client.
   */
  get countOnServer(): boolean {
    return this._countOnServer;
  }

  set countOnServer(value: boolean) {
    if (value != this.countOnServer) {
      this._countOnServer = wjcCore.asBoolean(value);
      this._queryData();
    }
  }

  /**
   * Gets or sets a value indicating whether to sort on the server or on the client.
   */
  get sortOnServer(): boolean {
    return this._sortOnServer;
  }

  set sortOnServer(value: boolean) {
    if (value != this.sortOnServer) {
      this._sortOnServer = wjcCore.asBoolean(value);
      this._queryData();
    }
  }

  /**
   * Gets or sets a value indicating whether to perform paging on the server or on the client.
   */
  get pageOnServer(): boolean {
    return this._pageOnServer;
  }

  set pageOnServer(value: boolean) {
    if (value != this.pageOnServer) {
      this._pageOnServer = wjcCore.asBoolean(value);
      this._queryData();
    }
  }

  /**
   * Gets or sets a value of page index.
   */
  // @ts-ignore: wijmo 5.20242 では property 宣言、旧実装は accessor override
  get pageIndex(): number {
    return this._pgIdx;
  }

  // @ts-ignore: wijmo 5.20242 では property 宣言、旧実装は accessor override

  set pageIndex(value: number) {
    this._pgIdx = wjcCore.asNumber(value);
  }

  /**
   * Gets or sets a value indicating whether to perform filtering on the server or on the client.
   */
  get filterOnServer(): boolean {
    return this._filterOnServer;
  }

  set filterOnServer(value: boolean) {
    if (value != this._filterOnServer) {
      this._filterOnServer = wjcCore.asBoolean(value);
      this._queryData();
    }
  }

  /**
   * Gets the total count of source items.
   */
  get totalCount(): number {
    return this._totalCount;
  }

  /**
   * Gets or sets the filter definition as a Breeze filter Predicate.
   */
  get filterPredicate(): any {
    return this._filterPredicate;
  }

  set filterPredicate(value: any) {
    if (value != this._filterPredicate) {
      this._filterPredicate = value;
      this._queryData();
    }
  }

  /**
   * Gets or sets the filter definition as a Breeze filter Predicate.
   */
  get filterSpecial(): string {
    return this._filterSpecial;
  }

  set filterSpecial(value: string) {
    if (value != this._filterSpecial) {
      this._filterSpecial = value;
      this._pgIdx = 0;
      this._idx = 0;
      this._queryData();
    }
  }

  /**
   * Gets or sets the filter definition as a Breeze filter Predicate.
   */
  get fetchPlan(): string {
    return this._fetchPlan;
  }

  set fetchPlan(value: string) {
    if (value != this._fetchPlan) {
      this._fetchPlan = value;
      this._queryData();
    }
  }

  /**
   * Gets or sets the callback function after result return.
   */
  get itemsSourceFunction(): Function {
    return this._itemsSourceFunction;
  }

  set itemsSourceFunction(value: Function) {
    this._itemsSourceFunction = wjcCore.asFunction(value);
  }

  get preSaveChangesFunction(): Function {
    return this._preSaveChangesFunction;
  }

  set preSaveChangesFunction(value: Function) {
    this._preSaveChangesFunction = wjcCore.asFunction(value);
  }


  get preDeleteItemsFunction(): Function {
    return this._preDeleteItemsFunction;
  }

  set preDeleteItemsFunction(value: Function) {
    this._preDeleteItemsFunction = wjcCore.asFunction(value);
  }

  /**
   * Gets or sets the binding map for mapping grid bindings to server field names.
   * Each entry maps a grid binding to { field: 'linkField', subField: 'property' }
   * for generating CONTAINS queries on linked collection fields.
   */
  get bindingMap(): { [key: string]: { field: string; subField: string; dataType?: string } } {
    return this._bindingMap;
  }

  set bindingMap(value: { [key: string]: { field: string; subField: string; dataType?: string } }) {
    this._bindingMap = value || {};
  }

  // dataMap の keyPath 値から元レコードの @rid を引く (RID 等価は link field の
  // index が効き、link 先 field の traversal フルスキャンを避けられる)
  // link field の先のクラス名を読み込み済み行の @class から判定する
  // (schema は link 先クラス未定義のため実データから推定)
  private _linkedClassOf(field: string): string | null {
    try {
      var items: any[] = (this.sourceCollection as any[]) || [];
      var n = Math.min(items.length, 500);
      for (var i = 0; i < n; i++) {
        var v = items[i] ? items[i][field] : null;
        if (v && typeof v === "object" && v["@class"]) {
          return v["@class"];
        }
      }
    } catch (e) { /* fallback */ }
    return null;
  }

  private _dataMapRid(dataMap: any, keyValue: any): string | null {
    try {
      var cv = dataMap.collectionView;
      var items = cv ? (cv.sourceCollection || cv.items) : null;
      if (items) {
        for (var i = 0; i < items.length; i++) {
          var it = items[i];
          if (it && it[dataMap._keyPath] == keyValue && it['@rid']) {
            return it['@rid'];
          }
        }
      }
    } catch (e) { /* fallback to traversal */ }
    return null;
  }

  // count 用に SELECT と FROM の間の射影を取り除く (最初の FROM までを置換。
  // 射影文字列中に "from" を含むクエリは対象外の従来経路で送られる想定)
  private _stripProjections(query: string): string {
    var m = /^\s*select\b[\s\S]*?\bfrom\b/i.exec(query);
    return m ? "SELECT FROM" + query.slice(m[0].length) : query;
  }

  // OrientDB 3.1 は日本語等の識別子を backtick 無しで書けない (Lexical error)。
  // sort / filter が組む field 名を backtick で quote する (ASCII でも無害)。
  private _quoteField(path: string): string {
    return String(path).split(".").map((seg) => /^`.*`$/.test(seg) ? seg : "`" + seg.split("`").join("") + "`").join(".");
  }

  private _resolveBinding(binding: string): string {
    var mapped = this._bindingMap[binding];
    if (mapped) {
      return mapped.field + '.' + mapped.subField;
    }
    return binding;
  }

  private _isMappedBinding(binding: string): boolean {
    return this._bindingMap.hasOwnProperty(binding);
  }

  private _isArrayBinding(binding: string): boolean {
    var mapped = this._bindingMap[binding];
    return mapped ? mapped.dataType === 'Array' : false;
  }

  /**
   *  Get sql string.
   */
  getSql(page: boolean): string {
    var sql = this._getServerFilterQuery(this._query);
    sql = this._getServerSortQuery(sql);
    if (page)
      sql = this._getServerPageQuery(sql);
    return sql;
  }

  /**
   * Updates the filter definition based on a known filter provider such as the
   * @see:wijmo.grid.FlexGridFilter.
   *
   * @param filterProvider Known filter provider, typically an instance of a
   * @see:wijmo.grid.filter.FlexGridFilter.
   */
  updateFilterDefinition(filterProvider: any) {
    if (this.filterOnServer && wjcGrid && wjcGridFilter && filterProvider instanceof wjcGridFilter.FlexGridFilter) {
      this.filterPredicate = this._asPredicate(filterProvider);
    }
  }

  /**
   * Occurs when the breeze query succeeds.
   */
  querySucceeded = new wjcCore.Event();

  /**
   * Raises the @see:querySucceeded event.
   *
   * @param e indicates the query data count.
   */
  onQuerySucceeded(e: QueryEventArgs) {
    this.querySucceeded.raise(this, e);
  }

  /**
   * Occurs when the breeze query fails.
   */
  queryFailed = new wjcCore.Event();

  /**
   * Raises the @see:queryFailed event.
   *
   * @param e indicates the fail information.
   */
  onQueryFailed(e: QueryEventArgs) {
    this.queryFailed.raise(this, e);
  }

  /**
   * Occurs when the save request success.
   */
  saveSucceeded = new wjcCore.Event();

  /**
   * Raises the @see:onSaveSucceeded event.
   *
   * @param e indicates the success information.
   */
  onSaveSucceeded(e: QueryEventArgs) {
    this.saveSucceeded.raise(this, e);
  }

  /**
   * Occurs when the save request fails.
   */
  saveFailed = new wjcCore.Event();

  /**
   * Raises the @see:onSaveFailed event.
   *
   * @param e indicates the fail information.
   */
  onSaveFailed(e: QueryEventArgs) {
    this.saveFailed.raise(this, e);
  }

  // ** implementation

  // gets the list that corresponds to the current page
  _getPageView() {
    return this.pageOnServer
      ? this._view
      : super._getPageView();
  }

  // disable sort and filter on client if we're doing it on the server
  _performRefresh() {
    // save settings
    var canFilter = this._canFilter,
      canSort = this._canSort;

    // perform refresh
    this._canFilter = !this._filterOnServer;
    this._canSort = !this._sortOnServer;
    super._performRefresh();

    // restore settings
    this._canFilter = canFilter;
    this._canSort = canSort;
  }

  // delete Items
  private _deleteItems(entities) {
    if (this._isSaving) {
      setTimeout(this._deleteItems.bind(this), 50);
      return;
    }
    this._isSaving = true;
    if (wjcCore.isFunction(this._preDeleteItemsFunction)) {
      entities = this._preDeleteItemsFunction(entities)
    }
    if (this.deleteSubscription) {
      this.deleteSubscription.unsubscribe();
    }
    this.deleteSubscription = this._api.delete(entities, this._url, this._tokenName).subscribe(this._saveSucceeded.bind(this), this._saveFailed.bind(this), this._saveFinished.bind(this));
  }

  clearData(): void {
    this._totalCount = 0;
    this.sourceCollection = [];
  }

  queryData(): void {
    this._queryData();
  }

  // send query for data
  private _queryData(): any {
    if (this.filterOnServer) {
      if (this._isSaving) {
        setTimeout(this._queryData.bind(this), 50);
        return;
      }
      var sql = this._getServerFilterQuery(this._query);
      if (this._group) {
        sql = sql + " GROUP BY " + this._group + " ";
      }
      // count は射影・ORDER BY を落とした "SELECT FROM <class> [WHERE ..]" 形式で送る。
      // backend (DataController) はこの形式なら SELECT COUNT(*) に書き換えて実行する
      // 高速経路を持つ (射影付きだと全行を実体化する副問合せになり大テーブルで数秒〜十数秒
      // かかる)。GROUP BY 時は件数の意味が変わるため従来どおりとする。
      var count = this._group
        ? sql
        : this._getServerFilterQuery(this._stripProjections(this._query));
      sql = this._getServerSortQuery(sql);
      if (!wjcCore.isNullOrWhiteSpace(this._count)) {
        count = this._count;
      }
      sql = this._getServerPageQuery(sql);
      this._isSaving = true;
      if (this.querySubscription) {
        this.querySubscription.unsubscribe();
      }
      if (this._countOnServer) {
        if (!wjcCore.isNullOrWhiteSpace(this._fetchPlan)) {
          this.querySubscription = this._api.query({
            sql: sql,
            count: count,
            fetchPlan: this._fetchPlan
          }, this._url, this._tokenName).subscribe(this._querySucceeded.bind(this), this._queryFailed.bind(this));
        } else {
          this.querySubscription = this._api.query({
            sql: sql,
            count: count
          }, this._url, this._tokenName).subscribe(this._querySucceeded.bind(this), this._queryFailed.bind(this));
        }
      } else {
        if (!wjcCore.isNullOrWhiteSpace(this._fetchPlan)) {
          this.querySubscription = this._api.query({
            sql: sql,
            fetchPlan: this._fetchPlan
          }, this._url, this._tokenName).subscribe(this._querySucceeded.bind(this), this._queryFailed.bind(this));
        } else {
          this.querySubscription = this._api.query({
            sql: sql,
          }, this._url, this._tokenName).subscribe(this._querySucceeded.bind(this), this._queryFailed.bind(this));
        }
      }
    }
  }

  private _querySucceeded(data) {
    if (data.inlineCount !== null && data.inlineCount !== undefined) {
      this._totalCount = data.inlineCount;
    }
    if (wjcCore.isFunction(this._itemsSourceFunction)) {
      this.sourceCollection = this._itemsSourceFunction(data.results)
    } else {
      this.sourceCollection = data.results;
    }
    this._isSaving = false;
    this.onQuerySucceeded(new QueryEventArgs(data.results.length));
  }

  private _queryFailed(error) {
    this._isSaving = false;
    this.onQueryFailed(new QueryEventArgs(error));
  }

  // gets the query with server sort
  private _getServerSortQuery(query: any): any {
    var strSort = '',
      sdCount = this.sortDescriptions.length,
      sorts = [];

    if (!query) {
      return;
    }

    // sort on server
    if (sdCount > 0 && this.canSort && this.sortOnServer) {
      for (var i = 0; i < sdCount; i++) {
        var sd = <wjcCore.SortDescription>this.sortDescriptions[i];
        strSort = this._quoteField(sd.property);
        if (!sd.ascending) {
          strSort += ' DESC';
        }
        sorts.push(strSort);
      }
      query = query + " ORDER BY " + sorts.join(",");
    }

    // return the query
    return query;
  }

  // gets the query with server paging
  private _getServerPageQuery(query: any): any {
    var skip = 0,
      s = '';
    if (!query) {
      return;
    }

    //apply page on server
    if (this._pageOnServer && this.pageSize) {
      skip = this._pgIdx * this.pageSize;
      if (skip) {
        s = " SKIP " + skip + " LIMIT " + this.pageSize;
      } else {
        s = " LIMIT " + this.pageSize;
      }
    }
    return query + s;
  }

  // get the query with server filtering
  private _getServerFilterQuery(query: any): any {
    if (query && this._filterPredicate && this._filterSpecial) {
      return query + " WHERE " + this._filterPredicate + " AND " + this._filterSpecial;
    } else if (query && this._filterPredicate) {
      return query + " WHERE " + this._filterPredicate;
    } else if (query && this._filterSpecial) {
      return query + " WHERE " + this._filterSpecial;
    } else {
      return query;
    }
  }

  // handle changes to the sort descriptors
  private _sortDescHandler() {
    var arr = this.sortDescriptions;
    for (var i = 0; i < arr.length; i++) {
      var sd = wjcCore.tryCast(arr[i], wjcCore.SortDescription);
      if (!sd) {
        throw 'sortDescriptions array must contain SortDescription objects.';
      }
    }
    if (this.canSort) {
      if (this.sortOnServer) {
        this._queryData();
      } else {
        this.refresh();
      }
    }
  }

  // save the changes
  private _saveChanges(entities) {
    if (this._isSaving) {
      setTimeout(this._saveChanges.bind(this), 50);
      return;
    }
    let docs: any[] = [];
    for (let entity of entities) {
      let keys: any[] = Object.keys(entity);
      keys.forEach((key: any) => {
        if (wjcCore.isObject(entity[key]) && !wjcCore.isUndefined(entity[key]["@rid"]) && !wjcCore.isNullOrWhiteSpace(entity[key]["@rid"])) {
          entity[key] = entity[key]["@rid"];
        }
        if (wjcCore.isArray(entity[key])) {
          let children: any[] = [];
          for (let subentity of entity[key]) {
            if (!wjcCore.isUndefined(subentity["@rid"]) && !wjcCore.isNullOrWhiteSpace(subentity["@rid"])) {
              children.push(subentity["@rid"]);
            } else {
              children.push(subentity);
            }
          }
          entity[key] = children;
        }
        if (wjcCore.isDate(entity[key])) {
          entity[key] = wjcCore.Globalize.formatDate(entity[key], 'yyyy-MM-dd HH:mm:ss')
        }
      });
      docs.push(entity);
    }
    this._isSaving = true;
    if (wjcCore.isFunction(this._preSaveChangesFunction)) {
      entities = this._preSaveChangesFunction(entities)
    }
    if (this.updateSubscription) {
      this.updateSubscription.unsubscribe();
    }
    this.updateSubscription = this._api.update(entities, this._url, this._tokenName).subscribe(this._saveSucceeded.bind(this), this._saveFailed.bind(this), this._saveFinished.bind(this));
  }

  private _saveSucceeded(saveResult) {
    this.onSaveSucceeded(new QueryEventArgs(saveResult));
    this._isSaving = false;
    this._queryData();
  }

  private _saveFailed(error) {
    this.onSaveFailed(new QueryEventArgs(error));
  }

  private _saveFinished() {
    this._isSaving = false;
  }

  // builds a Breeze Predicate based on a filter provider
  _asPredicate(filter: wjcGridFilter.FlexGridFilter): any {
    var predicate: any, p: any;
    var cols: any[] = [];
    if (filter.grid instanceof wjcMultirow.MultiRow) {
      for (var r = 0; r < filter.grid.rowsPerItem; r++) {
        for (var c = 0; c < filter.grid.columns.length; c++) {
          var col = filter.grid.getBindingColumn(filter.grid.cells, r, c);
          if (cols.indexOf(col.binding) == -1) {
            var cf = filter.getColumnFilter(col, false);
            if (cf && cf.isActive) {
              if (cf.conditionFilter && cf.conditionFilter.isActive) {
                p = this._asConditionFilterPredicate(cf.conditionFilter);
                predicate = predicate ? "( " + predicate + " AND " + p + " )" : p;
              } else if (cf.valueFilter && cf.valueFilter.isActive) {
                p = this._asValueFilterPredicate(cf.valueFilter);
                predicate = predicate ? "( " + predicate + " AND " + p + " )" : p;
              }
            }
            cols.push(col.binding);
          }
        }
      }
    } else {
      for (var c = 0; c < filter.grid.columns.length; c++) {
        var col = filter.grid.columns[c],
          cf = filter.getColumnFilter(col, false);
        if (cf && cf.isActive) {
          if (cf.conditionFilter && cf.conditionFilter.isActive) {
            p = this._asConditionFilterPredicate(cf.conditionFilter);
            predicate = predicate ? "( " + predicate + " AND " + p + " )" : p;
          } else if (cf.valueFilter && cf.valueFilter.isActive) {
            p = this._asValueFilterPredicate(cf.valueFilter);
            predicate = predicate ? "( " + predicate + " AND " + p + " )" : p;
          }
        }
      }
    }
    return predicate;
  }

  private _asValueFilterPredicate(vf: wjcGridFilter.ValueFilter): any {
    var col = vf.column,
      fld = this._resolveBinding(col.binding),
      isMapped = this._isMappedBinding(col.binding),
      isArr = this._isArrayBinding(col.binding),
      predicate: any,
      p: any;
    if (col.dataMap) {
      fld = fld + "." + col.dataMap._keyPath;
    }
    // dotted binding (売主.名称 等) は link 先 field の traversal でフルスキャンになる。
    // link 先クラスが判れば `売主` IN (SELECT FROM <cls> WHERE ...) へ書き換えて
    // link field index を効かせる (backend の新 executor 前提)
    var vfLinkHead: string | null = null, vfLinkCls: string | null = null;
    if (!col.dataMap && !isMapped && col.binding.indexOf(".") > 0) {
      var vfParts = col.binding.split(".");
      vfLinkCls = this._linkedClassOf(vfParts[0]);
      if (vfLinkCls) {
        vfLinkHead = this._quoteField(vfParts[0]);
        fld = vfParts.slice(1).join(".");
      }
    }
    fld = this._quoteField(fld);
    for (var key in vf.showValues) {
      var value = wjcCore.changeType(key, col.dataType, col.format);
      if (col.dataMap) {
        var keyValue = col.dataMap.getKeyValue(value);
        if (wjcCore.isFunction(col.dataMap["getData"])) {
          keyValue = col.dataMap["getData"](keyValue);
        }
        if (wjcCore.isObject(keyValue) && keyValue.hasOwnProperty(col.dataMap._keyPath)) {
          value = keyValue[col.dataMap._keyPath]
        }
        // RID 等価なら link field の index が効く (link 先 field の traversal は
        // 大テーブルでフルスキャン。kobe-ac 実測 6.6s → 0.2s)
        var rid = this._dataMapRid(col.dataMap, value);
        if (rid) {
          p = this._quoteField(col.binding) + " = " + rid;
          predicate = predicate ? "( " + predicate + " OR " + p + " )" : p;
          continue;
        }
      }
      if (isMapped && isArr) {
        var mapped = this._bindingMap[col.binding];
        if (wjcCore.isString(value) && wjcCore.isNullOrWhiteSpace(value)) {
          p = mapped.field + ".size() = 0";
        } else {
          p = mapped.field + " CONTAINS (" + mapped.subField + " = '" + value + "')";
        }
      } else if (isMapped) {
        if (wjcCore.isString(value) && wjcCore.isNullOrWhiteSpace(value)) {
          p = fld + " IS NULL";
        } else {
          p = fld + " = '" + value + "'";
        }
      } else if (wjcCore.isDate(value)) {
        p = fld + " = DATE('" + wjcCore.Globalize.formatDate(value, 'yyyy-MM-dd HH:mm:ss') + "','yyyy-MM-dd HH:mm:ss')";
      } else if (col.dataType == wjcCore.DataType.Array) {
        if (wjcCore.isString(value) && wjcCore.isNullOrWhiteSpace(value)) {
          p = fld + " = []";
        } else {
          p = fld + " IN ['" + value + "']";
        }
      } else {
        if (wjcCore.isString(value) && wjcCore.isNullOrWhiteSpace(value)) {
          p = fld + " IS NULL";
        } else {
          p = fld + " = '" + value + "'";
        }
      }
      // IS NULL は link 自体が null の行も対象のため従来 traversal のまま
      if (vfLinkCls && p && p.indexOf(" IS NULL") < 0) {
        p = vfLinkHead + " IN (SELECT FROM `" + vfLinkCls + "` WHERE " + p + ")";
      }
      predicate = predicate ? "( " + predicate + " OR " + p + " )" : p;
    }
    return predicate;
  }

  private _asConditionFilterPredicate(cf: wjcGridFilter.ConditionFilter): any {
    var predicate = this._asConditionPredicate(cf, cf.condition1);
    if (cf.condition2.operator != null) {
      var p = this._asConditionPredicate(cf, cf.condition2);
      if (cf.and) {
        predicate = "( " + predicate + " AND " + p + " )";
      } else {
        predicate = "( " + predicate + " OR " + p + " )";
      }
    }
    return predicate;
  }

  private _asConditionPredicate(cf: wjcGridFilter.ConditionFilter, cond: wjcGridFilter.FilterCondition): any {
    var p = "",
      fld = this._resolveBinding(cf.column.binding),
      isMapped = this._isMappedBinding(cf.column.binding),
      isArr = this._isArrayBinding(cf.column.binding),
      value = cond.value
    // dotted binding は link 副問合せへ (詳細は _asValueFilterPredicate 参照)
    var cpLinkHead: string | null = null, cpLinkCls: string | null = null;
    if (!cf.column.dataMap && !isMapped && fld.indexOf(".") > 0) {
      var cpParts = fld.split(".");
      cpLinkCls = this._linkedClassOf(cpParts[0]);
      if (cpLinkCls) {
        cpLinkHead = this._quoteField(cpParts[0]);
        fld = cpParts.slice(1).join(".");
      }
    }
    if (cf.column.dataMap) {
      fld = fld + "." + cf.column.dataMap._keyPath;
      var keyValue = cf.column.dataMap.getKeyValue(value);
      if (wjcCore.isObject(keyValue) && keyValue.hasOwnProperty(cf.column.dataMap._keyPath)) {
        value = keyValue[cf.column.dataMap._keyPath]
      }
    }
    fld = this._quoteField(fld);
    // 等価/不等価は RID 比較にして link field の index を効かせる (traversal 回避)
    if (cf.column.dataMap && (cond.operator === 0 || cond.operator === 1)) {
      var ridEq = this._dataMapRid(cf.column.dataMap, value);
      if (ridEq) {
        return this._quoteField(cf.column.binding) + (cond.operator === 0 ? " = " : " <> ") + ridEq;
      }
    }
    if (isMapped && isArr) {
      var mapped = this._bindingMap[cf.column.binding];
      switch (cond.operator) {
        case 0: // EQ
          if (wjcCore.isNullOrWhiteSpace(value) || value == "undefined") {
            p = mapped.field + ".size() = 0";
          } else {
            p = mapped.field + " CONTAINS (" + mapped.subField + " = '" + value + "')";
          }
          break;
        case 1: // NE
          p = "NOT (" + mapped.field + " CONTAINS (" + mapped.subField + " = '" + value + "'))";
          break;
        case 6: // BW
          p = mapped.field + " CONTAINS (" + mapped.subField + " LIKE '" + value + "%')";
          break;
        case 7: // EW
          p = mapped.field + " CONTAINS (" + mapped.subField + " LIKE '%" + value + "')";
          break;
        case 8: // CT
          p = mapped.field + " CONTAINS (" + mapped.subField + " LIKE '%" + value + "%')";
          break;
        case 9: // NC
          p = "NOT (" + mapped.field + " CONTAINS (" + mapped.subField + " LIKE '%" + value + "%'))";
          break;
        default:
          p = mapped.field + " CONTAINS (" + mapped.subField + " = '" + value + "')";
          break;
      }
      return p;
    }
    switch (cond.operator) {
      case 0: // EQ = 0,
        if (wjcCore.isDate(cond.value)) {
          p = "(" + fld + " >= DATE('" + wjcCore.Globalize.formatDate(cond.value, 'yyyy-MM-dd HH:mm:ss') + "','yyyy-MM-dd HH:mm:ss') AND " + fld + " < DATE('" + wjcCore.Globalize.formatDate(wjcCore.DateTime.addDays(cond.value, 1), 'yyyy-MM-dd HH:mm:ss') + "','yyyy-MM-dd HH:mm:ss'))";
        } else {
          if(wjcCore.isNullOrWhiteSpace(value) || value == "undefined"){
            p = fld + " IS NULL";
          } else {
            p = fld + " = '" + value + "'";
          }
        }
        break;
      case 1: // NE = 1,
        p = fld + " <> '" + value + "'";
        break;
      case 2: // GT = 2,
        if (wjcCore.isDate(cond.value)) {
          p = fld + " > DATE('" + wjcCore.Globalize.formatDate(cond.value, 'yyyy-MM-dd HH:mm:ss') + "','yyyy-MM-dd HH:mm:ss')";
        } else {
          p = fld + " > " + value;
        }
        break;
      case 3: // GE = 3,
        if (wjcCore.isDate(cond.value)) {
          p = fld + " >= DATE('" + wjcCore.Globalize.formatDate(cond.value, 'yyyy-MM-dd HH:mm:ss') + "','yyyy-MM-dd HH:mm:ss')";
        } else {
          p = fld + " >= " + value;
        }
        break;
      case 4: // LT = 4,
        if (wjcCore.isDate(cond.value)) {
          p = fld + " < DATE('" + wjcCore.Globalize.formatDate(cond.value, 'yyyy-MM-dd HH:mm:ss') + "','yyyy-MM-dd HH:mm:ss')";
        } else {
          p = fld + " < " + value;
        }
        break;
      case 5: // LE = 5,
        if (wjcCore.isDate(cond.value)) {
          p = fld + " <= DATE('" + wjcCore.Globalize.formatDate(cond.value, 'yyyy-MM-dd HH:mm:ss') + "','yyyy-MM-dd HH:mm:ss')";
        } else {
          p = fld + " <= " + value;
        }
        break;
      case 6: // BW = 6,
        p = fld + " LIKE '" + value + "%'";
        break;
      case 7: // EW = 7,
        p = fld + " LIKE '%" + value + "'";
        break;
      case 8: // CT = 8,
        p = fld + " LIKE '%" + value + "%'";
        break;
      case 9: // NC = 9
        p = "NOT( " + fld + " LIKE '%" + value + "%' )";
        break;
    }
    // dotted binding は link 副問合せに包む (index を効かせる)
    if (cpLinkCls && p) {
      p = cpLinkHead + " IN (SELECT FROM `" + cpLinkCls + "` WHERE " + p + ")";
    }
    return p;
  }
}

export class QueryEventArgs extends wjcCore.EventArgs {
  _data: any;

  constructor(data) {
    super();
    this._data = data;
  }

  get data(): any {
    return this._data;
  }
}
